import { create } from "zustand";

export type MagicMomentView =
  | "main"
  | "magic-moment"
  | "tips"
  | "tip-detail";

interface MagicMomentStore {
  bannerExpanded: boolean;
  currentView: MagicMomentView;
  selectedTip: string | null;
  navigationHistory: MagicMomentView[];
  modalDismissedLocally: Set<string>;

  toggleBanner: () => void;
  expand: () => void;
  collapse: () => void;
  navigateToView: (view: MagicMomentView) => void;
  selectTip: (tip: string) => void;
  goBack: () => void;
  resetView: () => void;
  dismissModal: (mentorshipId: string) => void;
  isModalDismissed: (mentorshipId: string) => boolean;
}

export const useMagicMomentStore = create<MagicMomentStore>((set, get) => ({
  bannerExpanded: true,
  currentView: "main",
  selectedTip: null,
  navigationHistory: [],
  modalDismissedLocally: new Set(),

  toggleBanner: () =>
    set((state) => ({ bannerExpanded: !state.bannerExpanded })),

  expand: () => set({ bannerExpanded: true }),

  collapse: () => set({ bannerExpanded: false }),

  navigateToView: (view: MagicMomentView) => {
    const currentView = get().currentView;
    set((state) => ({
      currentView: view,
      navigationHistory: [...state.navigationHistory, currentView],
    }));
  },

  selectTip: (tip: string) => {
    const currentView = get().currentView;
    set((state) => ({
      selectedTip: tip,
      currentView: "tip-detail",
      navigationHistory: [...state.navigationHistory, currentView],
    }));
  },

  goBack: () => {
    const history = get().navigationHistory;
    if (history.length > 0) {
      const previousView = history[history.length - 1];
      set((state) => ({
        currentView: previousView,
        selectedTip: previousView === "main" ? null : state.selectedTip,
        navigationHistory: state.navigationHistory.slice(0, -1),
      }));
    } else {
      set({ currentView: "main", selectedTip: null });
    }
  },

  resetView: () =>
    set({
      currentView: "main",
      selectedTip: null,
      navigationHistory: [],
      bannerExpanded: true,
    }),

  // Local optimistic dismissal — the BE persist is called separately via
  // useUpdateMagicMomentState() from Day2Modal.
  dismissModal: (mentorshipId: string) =>
    set((state) => {
      const next = new Set(state.modalDismissedLocally);
      next.add(mentorshipId);
      return { modalDismissedLocally: next };
    }),

  isModalDismissed: (mentorshipId: string) =>
    get().modalDismissedLocally.has(mentorshipId),
}));
